import React, { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Goal, AlertTriangle, UserMinus, Shield, Trash2, Plus,
  Camera, Video, Image as ImageIcon, X, Upload, Play
} from 'lucide-react';
import { getInitials, resolveMediaUrl } from '@/lib/utils';

// Event type configurations
export const EVENT_TYPES = {
  goal: { label: 'Gol', icon: Goal, color: 'bg-green-500', emoji: '⚽' },
  penalty: { label: 'Rigore', icon: Goal, color: 'bg-green-600', emoji: '🎯' },
  own_goal: { label: 'Autogol', icon: Goal, color: 'bg-red-500', emoji: '🔴' },
  yellow_card: { label: 'Cartellino Giallo', icon: AlertTriangle, color: 'bg-yellow-500', emoji: '🟨' },
  red_card: { label: 'Cartellino Rosso', icon: AlertTriangle, color: 'bg-red-600', emoji: '🟥' },
  assist: { label: 'Assist', icon: Shield, color: 'bg-blue-500', emoji: '👟' },
  injury: { label: 'Infortunio', icon: UserMinus, color: 'bg-orange-500', emoji: '🏥' },
  substitution: { label: 'Sostituzione', icon: UserMinus, color: 'bg-gray-500', emoji: '🔄' },
};

// Match Events Section
export const MatchEventsSection = ({ matchId, participants, isOrganizer, teamA, teamB }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);

  const fetchEvents = async () => {
    try {
      const res = await api.get(`/matches/${matchId}/events`);
      setEvents(res.data.events || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [matchId]);

  const handleDeleteEvent = async (eventId) => {
    if (!confirm('Sei sicuro di voler eliminare questo evento?')) return;
    
    try {
      await api.delete(`/matches/${matchId}/events/${eventId}`);
      toast.success('Evento eliminato');
      fetchEvents();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  // Group events by type for summary
  const goalsByTeam = {
    A: events.filter(e => (e.event_type === 'goal' || e.event_type === 'penalty') && e.team === 'A'),
    B: events.filter(e => (e.event_type === 'goal' || e.event_type === 'penalty') && e.team === 'B'),
  };

  const cards = events.filter(e => e.event_type === 'yellow_card' || e.event_type === 'red_card');

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Goal className="h-5 w-5" />
          Eventi Partita
        </CardTitle>
        {isOrganizer && (
          <Button size="sm" onClick={() => setShowAddDialog(true)} data-testid="add-event-btn">
            <Plus className="h-4 w-4 mr-1" />
            Aggiungi
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-muted-foreground text-center py-4">Caricamento...</p>
        ) : events.length === 0 ? (
          <p className="text-muted-foreground text-center py-4">
            Nessun evento registrato
          </p>
        ) : (
          <div className="space-y-4">
            {/* Scorers Summary */}
            {(goalsByTeam.A.length > 0 || goalsByTeam.B.length > 0) && (
              <div className="grid grid-cols-2 gap-4 p-3 bg-muted rounded-lg">
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2">Team A - Marcatori</p>
                  {goalsByTeam.A.length > 0 ? (
                    <div className="space-y-1">
                      {goalsByTeam.A.map(e => (
                        <p key={e.event_id} className="text-sm">
                          ⚽ {e.player?.name?.split(' ')[0] || '?'} {e.minute ? `(${e.minute}')` : ''}
                        </p>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">-</p>
                  )}
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2">Team B - Marcatori</p>
                  {goalsByTeam.B.length > 0 ? (
                    <div className="space-y-1">
                      {goalsByTeam.B.map(e => (
                        <p key={e.event_id} className="text-sm">
                          ⚽ {e.player?.name?.split(' ')[0] || '?'} {e.minute ? `(${e.minute}')` : ''}
                        </p>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">-</p>
                  )}
                </div>
              </div>
            )}

            {/* Timeline */}
            <ScrollArea className="max-h-64">
              <div className="space-y-2">
                {events.map((event) => {
                  const config = EVENT_TYPES[event.event_type] || {};
                  return (
                    <div 
                      key={event.event_id} 
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 group"
                    >
                      <div className="w-10 text-center">
                        {event.minute && (
                          <span className="text-sm font-mono text-muted-foreground">{event.minute}&apos;</span>
                        )}
                      </div>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white ${config.color || 'bg-gray-500'}`}>
                        <span className="text-sm">{config.emoji || '📝'}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">
                          {config.label || event.event_type}
                          {event.team && <Badge variant="outline" className="ml-2 text-xs">Team {event.team}</Badge>}
                        </p>
                        <p className="text-sm text-muted-foreground truncate">
                          {event.player?.name || 'Giocatore'}
                          {event.secondary_player && ` → ${event.secondary_player.name}`}
                          {event.note && ` - ${event.note}`}
                        </p>
                      </div>
                      {isOrganizer && (
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="opacity-0 group-hover:opacity-100"
                          onClick={() => handleDeleteEvent(event.event_id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </ScrollArea>

            {/* Cards summary */}
            {cards.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-2 border-t">
                {cards.map(c => (
                  <Badge 
                    key={c.event_id} 
                    variant={c.event_type === 'red_card' ? 'destructive' : 'warning'}
                    className="text-xs"
                  >
                    {c.event_type === 'red_card' ? '🟥' : '🟨'} {c.player?.name?.split(' ')[0]}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Add Event Dialog */}
        <AddEventDialog
          open={showAddDialog}
          onOpenChange={setShowAddDialog}
          matchId={matchId}
          teamA={teamA}
          teamB={teamB}
          onSuccess={fetchEvents}
        />
      </CardContent>
    </Card>
  );
};

// Add Event Dialog
const AddEventDialog = ({ open, onOpenChange, matchId, teamA, teamB, onSuccess }) => {
  const [eventType, setEventType] = useState('goal');
  const [team, setTeam] = useState('A');
  const [playerId, setPlayerId] = useState('');
  const [secondaryPlayerId, setSecondaryPlayerId] = useState('');
  const [minute, setMinute] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const players = team === 'A' ? teamA : teamB;
  const needsSecondary = eventType === 'assist' || eventType === 'substitution';

  const handleSubmit = async () => {
    if (!playerId) {
      toast.error('Seleziona un giocatore');
      return;
    }

    setSaving(true);
    try {
      await api.post(`/matches/${matchId}/events`, {
        event_type: eventType,
        team,
        player_id: playerId,
        secondary_player_id: needsSecondary ? secondaryPlayerId : undefined,
        minute: minute ? parseInt(minute) : undefined,
        note
      });
      toast.success('Evento aggiunto');
      onOpenChange(false);
      onSuccess();
      
      // Reset form
      setPlayerId('');
      setSecondaryPlayerId('');
      setMinute('');
      setNote('');
    } catch (error) {
      toast.error('Errore nell\'aggiunta');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Aggiungi Evento</DialogTitle>
          <DialogDescription>Registra un evento della partita</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Event Type */}
          <div className="space-y-2">
            <Label>Tipo Evento</Label>
            <Select value={eventType} onValueChange={setEventType}>
              <SelectTrigger data-testid="event-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(EVENT_TYPES).map(([key, config]) => (
                  <SelectItem key={key} value={key}>
                    {config.emoji} {config.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Team */}
          <div className="space-y-2">
            <Label>Squadra</Label>
            <Select value={team} onValueChange={setTeam}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="A">Team A</SelectItem>
                <SelectItem value="B">Team B</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Player */}
          <div className="space-y-2">
            <Label>Giocatore</Label>
            <Select value={playerId} onValueChange={setPlayerId}>
              <SelectTrigger data-testid="player-select">
                <SelectValue placeholder="Seleziona giocatore" />
              </SelectTrigger>
              <SelectContent>
                {players.map(p => (
                  <SelectItem key={p.user_id} value={p.user_id}>
                    {p.user?.name || p.name || 'Giocatore'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Secondary Player (for assists/substitutions) */}
          {needsSecondary && (
            <div className="space-y-2">
              <Label>{eventType === 'assist' ? 'Marcatore' : 'Giocatore Entrante'}</Label>
              <Select value={secondaryPlayerId} onValueChange={setSecondaryPlayerId}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona giocatore" />
                </SelectTrigger>
                <SelectContent>
                  {players.map(p => (
                    <SelectItem key={p.user_id} value={p.user_id}>
                      {p.user?.name || p.name || 'Giocatore'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Minute */}
          <div className="space-y-2">
            <Label>Minuto (opzionale)</Label>
            <Input
              type="number"
              min="1"
              max="120"
              value={minute}
              onChange={(e) => setMinute(e.target.value)}
              placeholder="es. 35"
              data-testid="minute-input"
            />
          </div>

          {/* Note */}
          <div className="space-y-2">
            <Label>Note (opzionale)</Label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="es. Tiro da fuori area"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="save-event-btn">
            {saving ? 'Salvataggio...' : 'Aggiungi Evento'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Match Media Gallery
export const MatchMediaGallery = ({ matchId, isOrganizer, isParticipant }) => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);

  const canUpload = isOrganizer || isParticipant;

  const fetchMedia = async () => {
    try {
      const res = await api.get(`/matches/${matchId}/media`);
      setMedia(res.data.media || []);
    } catch (error) {
      console.error('Error fetching media:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMedia();
  }, [matchId]);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        await api.post(`/matches/${matchId}/media`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        toast.success(`${file.name} caricato`);
      } catch (error) {
        toast.error(`Errore nel caricamento di ${file.name}`);
      }
    }
    
    setUploading(false);
    fetchMedia();
    e.target.value = '';
  };

  const handleDelete = async (mediaId) => {
    if (!confirm('Sei sicuro di voler eliminare questo file?')) return;
    
    try {
      await api.delete(`/matches/${matchId}/media/${mediaId}`);
      toast.success('File eliminato');
      fetchMedia();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const photos = media.filter(m => m.media_type === 'photo');
  const videos = media.filter(m => m.media_type === 'video');

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Camera className="h-5 w-5" />
          Foto e Video
        </CardTitle>
        {canUpload && (
          <div className="relative">
            <input
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={handleUpload}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              disabled={uploading}
            />
            <Button size="sm" disabled={uploading} data-testid="upload-media-btn">
              <Upload className="h-4 w-4 mr-1" />
              {uploading ? 'Caricamento...' : 'Carica'}
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-muted-foreground text-center py-4">Caricamento...</p>
        ) : media.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Camera className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>Nessun media caricato</p>
            {canUpload && (
              <p className="text-sm mt-1">Carica foto e video della partita!</p>
            )}
          </div>
        ) : (
          <Tabs defaultValue="photos">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="photos" className="flex items-center gap-1">
                <ImageIcon className="h-4 w-4" />
                Foto ({photos.length})
              </TabsTrigger>
              <TabsTrigger value="videos" className="flex items-center gap-1">
                <Video className="h-4 w-4" />
                Video ({videos.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="photos" className="mt-4">
              {photos.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">Nessuna foto</p>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {photos.map((photo) => (
                    <div 
                      key={photo.media_id} 
                      className="relative aspect-square rounded-lg overflow-hidden group cursor-pointer"
                      onClick={() => setSelectedMedia(photo)}
                    >
                      <img 
                        src={resolveMediaUrl(photo.url)} 
                        alt={photo.caption || 'Foto partita'}
                        className="w-full h-full object-cover"
                      />
                      {(isOrganizer || photo.uploaded_by === isParticipant) && (
                        <Button
                          size="icon"
                          variant="destructive"
                          className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => { e.stopPropagation(); handleDelete(photo.media_id); }}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="videos" className="mt-4">
              {videos.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">Nessun video</p>
              ) : (
                <div className="space-y-2">
                  {videos.map((video) => (
                    <div 
                      key={video.media_id} 
                      className="flex items-center gap-3 p-2 rounded-lg bg-muted group"
                    >
                      <div className="h-16 w-24 rounded bg-black flex items-center justify-center relative overflow-hidden">
                        <video 
                          src={resolveMediaUrl(video.url)} 
                          className="w-full h-full object-cover"
                          preload="metadata"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <Play className="h-6 w-6 text-white" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{video.original_filename || 'Video'}</p>
                        <p className="text-xs text-muted-foreground">
                          {video.uploader?.name || 'Utente'} • {(video.file_size / 1024 / 1024).toFixed(1)} MB
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => window.open(resolveMediaUrl(video.url), '_blank')}
                        >
                          <Play className="h-4 w-4" />
                        </Button>
                        {isOrganizer && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="text-destructive"
                            onClick={() => handleDelete(video.media_id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}

        {/* Lightbox Dialog */}
        <Dialog open={!!selectedMedia} onOpenChange={() => setSelectedMedia(null)}>
          <DialogContent className="max-w-4xl p-0 overflow-hidden">
            {selectedMedia && (
              <img 
                src={resolveMediaUrl(selectedMedia.url)} 
                alt={selectedMedia.caption || 'Foto'}
                className="w-full h-auto"
              />
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default { MatchEventsSection, MatchMediaGallery };
